import React, { useEffect, useState, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import CircularProgress from "@mui/material/CircularProgress";
import { filterCorruptFile, handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import RemovePdfModal from "../modals/RemovePdfModal";
import DriveFilePicker from "./DriveFilePicker";
import {
  Container,
  DragAndDrop,
  HeaderText,
  IconDiv,
  InputContainer,
  LeftPane,
  LeftPaneHeader,
  RightPane,
  SupportText,
  UploadText,
  Input,
  ChatDiv,
  ChatBox,
  StrongText,
  ChatText,
  QuestionDiv,
  PdfContainer,
  Header,
  Heading,
  ContentDiv,
  UserChatDiv,
  UserChatBox,
  ScrollableChat,
  ClearButton,
  LoadingText,
  DriveUploadBtn,
  OrDivider,
  ErrorText,
} from "./Style";
import PdfViewer from "../../Components/PdfViewer/PdfViewer";
import DrivePdfViewer from "../DrivePdfViewer";
import UploadIcon from "../../assets/upload.svg";
import ArrowUpIcon from "../../assets/arrow-up.svg";
import BlueIcon from "../../assets/blue-icon.svg";
import ChatIcon from "../../assets/chat.png";
import BackIcon from "../../assets/back-icon.svg";
import Drive from "../../assets/Gdrive.png";
import CrossIcon from "../../assets/cross-in-square.svg";
import { toast } from "react-toastify";
import { MAX_FILE_MB, MAX_FILE_SIZE, API_BASE_URL } from "../../constant/constant";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

//Chat with Document component

export default function Summarize() {
  const [file, setFile] = useState(null);
  const [showRemoveFileModal, setShowRemoveFileModal] = useState(false);
  const { isSidebarOpen } = useOutletContext();
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [userMessages, setUserMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [objectKey, setObjectKey] = useState("");
  const [conversation, setConversation] = useState([]);
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [driveFiles, setDriveFiles] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const navigate = useNavigate();
  const scrollableChatRef = useRef(null);
  const [avatarData, setAvatarData] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState(t('summarize.welcomeMessage'));

  // Polling function to check processing status
  const pollProcessingStatus = async (sessionId) => {
    const maxAttempts = 500;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(
          `https://dataprocessor-ks.aivoria.nl/get_processing_status/${sessionId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Processing status:", data);

        if (data.status === "completed") {
          return data;
        } else if (data.status === "error") {
          throw new Error(data.error || t('summarize.errors.processingError'));
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      } catch (error) {
        console.error("Error checking processing status:", error);
        throw error;
      }
    }

    throw new Error(t('summarize.processing.timeout'));
  };

  const handleDriveFiles = async (links) => {
    const fileId = links[0];
    const apiKey = process.env.REACT_APP_API_KEY;
    const fileUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&key=${apiKey}`;
    let response = null;
    
    try {
      response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      
      if (!response.ok) {
        response = await fetch(fileUrl);
        if (!response.ok) {
          const errorData = await response.json();
          console.error("File fetch error:", errorData);
          if (response.status === 404) {
            toast.error(t('summarize.errors.fileNotFound'));
            return;
          }
          throw new Error(t('summarize.errors.fetchError', { error: response.statusText }));
        }
      }

      const blob = await response.blob();
      const selectedFile = new File([blob], "downloaded_file.pdf", {
        type: "application/pdf",
      });

      setLoadingFile(true);
      setFile(selectedFile);
      setError(null);

      const isValid = await filterCorruptFile(selectedFile);
      if (!isValid) {
        setError(t('summarize.errors.corruptedFile'));
        toast.error(t('summarize.errors.corruptedFile'));
        setLoadingFile(false);
        return;
      }
      
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError(t('summarize.errors.fileSizeExceeds', { size: MAX_FILE_MB }));
        toast.error(t('summarize.errors.fileSizeExceeds', { size: MAX_FILE_MB }));
        setLoadingFile(false);
        return;
      }

      const formData = new FormData();
      formData.append("files", selectedFile);

      const uploadResponse = await fetch(
        "https://dataprocessor-ks.aivoria.nl/get_pdf_content",
        {
          method: "POST",
          body: formData,
        }
      );
      const uploadData = await uploadResponse.json();
      console.log("Upload response from server:", uploadData);

      const completedData = await pollProcessingStatus(uploadData.session_id);
      setObjectKey(completedData.model_path);

      const fileURL = URL.createObjectURL(blob);
      setFile(fileURL);

      setError(null);
      setConversation([]);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(t('summarize.errors.processingError'));
      toast.error(t('summarize.errors.processingError'));
    } finally {
      setLoadingFile(false);
    }
  };

  const handleFileChange = async (event) => {
    setError(null);
    const selectedFile = event.target.files[0];
    
    if (selectedFile && selectedFile.type === "application/pdf") {
      const isValid = await filterCorruptFile(selectedFile);
      if (!isValid) {
        setError(t('summarize.errors.corruptedFile'));
        toast.error(t('summarize.errors.corruptedFile'));
        return;
      }
      
      console.log("file size: ", selectedFile.size);
      
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError(t('summarize.errors.fileSizeExceeds', { size: MAX_FILE_MB }));
        toast.error(t('summarize.errors.fileSizeExceeds', { size: MAX_FILE_MB }));
        return;
      }
      
      setLoadingFile(true);
      setFile(selectedFile);
      const formData = new FormData();
      formData.append("files", selectedFile);

      try {
        const response = await fetch("https://dataprocessor-ks.aivoria.nl/get_pdf_content", {
          method: "POST",
          body: formData,
        });
        const uploadData = await response.json();
        console.log("Upload response from server:", uploadData);

        const completedData = await pollProcessingStatus(uploadData.session_id);
        setObjectKey(completedData.model_path);

        setFile(URL.createObjectURL(selectedFile));
        setError(null);
        setConversation([]);
      } catch (error) {
        console.error("Error uploading PDF:", error);
        setError(t('summarize.errors.processingError'));
        toast.error(t('summarize.errors.processingError'));
      } finally {
        setLoadingFile(false);
      }
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const selectedFile = event.dataTransfer.files[0];
    
    if (selectedFile && selectedFile.type === "application/pdf") {
      const isValid = await filterCorruptFile(selectedFile);
      if (!isValid) {
        setError(t('summarize.errors.corruptedFile'));
        toast.error(t('summarize.errors.corruptedFile'));
        return;
      }
      
      console.log("file size: ", selectedFile.size);
      
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError(t('summarize.errors.fileSizeExceeds', { size: MAX_FILE_MB }));
        toast.error(t('summarize.errors.fileSizeExceeds', { size: MAX_FILE_MB }));
        return;
      }
      
      setLoadingFile(true);
      setFile(URL.createObjectURL(selectedFile));
      const formData = new FormData();
      formData.append("files", selectedFile);

      try {
        const response = await fetch("https://dataprocessor-ks.aivoria.nl/get_pdf_content", {
          method: "POST",
          body: formData,
        });
        const uploadData = await response.json();
        console.log("Upload response from server:", uploadData);

        const completedData = await pollProcessingStatus(uploadData.session_id);
        setObjectKey(completedData.model_path);

        setError(null);
        setConversation([]);
      } catch (error) {
        console.error("Error uploading PDF:", error);
        setError(t('summarize.errors.processingError'));
        toast.error(t('summarize.errors.processingError'));
      } finally {
        setLoadingFile(false);
      }
    } else {
      setError(t('summarize.errors.invalidPdf'));
      toast.error(t('summarize.errors.invalidPdf'));
    }
  };

  const getAvatarData = async () => {
    try {
      const userId = localStorage.getItem("id");
      const response = await fetch(
        `${API_BASE_URL}/api/auth/get-avatar?user_id=${userId}`
      );
      const data = await response.json();
      if (data.responseCode === 404) {
        navigate("/avatar-selection");
        return;
      }
      if (data.responseCode === 200) {
        setAvatarData(data.avatar);
      }
    } catch (error) {
      console.error("Error fetching avatar data:", error);
    }
  };

  useEffect(() => {
    getAvatarData();
  }, []);

  useEffect(() => {
    if (avatarData && avatarData.greetingLanguage === "dutch") {
      setWelcomeMessage(t('summarize.welcomeMessage'));
    } else {
      setWelcomeMessage("Hello! I am Chat2doc Ai. How may I assist you?");
    }
  }, [avatarData, t]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() !== "") {
      const userQuery = {
        role: "USER",
        message: inputValue,
      };
      const newMessages = [...conversation, userQuery];
      setConversation(newMessages);
      setInputValue("");
      setLoadingMessage(true);

      const requestBody = {
        user_query: newMessages,
        language: avatarData?.greetingLanguage || 'english',
        object_key: objectKey,
      };

      try {
        const response = await fetch(
          "https://langchain-ks.aivoria.nl/chat_with_pdf",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(t('summarize.errors.aiResponseFailed'));
        }

        const data = await response.json();
        console.log("AI response:", data);

        const aiResponse = {
          role: "ASSISTANT",
          message: data.response,
        };

        setConversation((prevConversation) => [
          ...prevConversation,
          aiResponse,
        ]);
      } catch (error) {
        console.error("Error getting response from AI:", error);
        toast.error(t('summarize.errors.aiResponseFailed'));
      } finally {
        setLoadingMessage(false);
      }
    }
  };

  const uploadFile = async () => {
    if (file) {
      try {
        const response = await handleFileUploadWithSignedURL(file);
        return response.fileUrl;
      } catch (error) {
        toast.error(error);
      }
    }
  };

  useEffect(() => {
    if (scrollableChatRef.current) {
      scrollableChatRef.current.scrollTop =
        scrollableChatRef.current.scrollHeight;
    }
  }, [conversation]);

  const handleClearMessages = () => {
    setConversation([]);
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (typeof file === 'string') {
      URL.revokeObjectURL(file);
    }
    handleClearMessages();
  };

  const handleShowRemoveFileModal = () => {
    setShowRemoveFileModal(true);
  };

  const handleHideRemoveFileModal = () => {
    setShowRemoveFileModal(false);
  };

  return (
    <Container isOpen={isSidebarOpen}>
      <Header onClick={() => navigate("/")}>
        <img src={BackIcon} alt={t('summarize.back')} />
        <Heading>{t('summarize.title')}</Heading>

        <Tooltip
            title={t("summarize.tooltip")}
            placement="top"
          >
            <InfoCircleOutlined
              style={{
                fontSize: 14,
                color: "#8c8c8c",
                cursor: "pointer",
              }}
              onClick={(e) => e.stopPropagation()} // prevent back navigation
            />
          </Tooltip>
      </Header>
      <ContentDiv>
        <LeftPane>
          <div style={{ display: "flex", gap: "20px", marginBottom: "0.5rem" }}>
            <LeftPaneHeader style={{ width: "100%" }}>
              <HeaderText>{t('summarize.askChatDoc')}</HeaderText>
            </LeftPaneHeader>
            <ClearButton onClick={handleClearMessages}>
              {t('summarize.clear')}
            </ClearButton>
          </div>
          <ScrollableChat ref={scrollableChatRef}>
            {loadingFile ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
                <ChatText style={{ marginLeft: "1rem" }}>
                  {t('summarize.aiModelLoading')}
                </ChatText>
              </div>
            ) : file ? (
              <>
                <ChatDiv>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <ChatBox>
                      <ChatText>{welcomeMessage}</ChatText>
                    </ChatBox>
                  </div>
                </ChatDiv>
                {conversation.map((message, index) =>
                  message.role === "USER" ? (
                    <UserChatDiv key={index}>
                      <UserChatBox>
                        <ChatText>{message.message}</ChatText>
                      </UserChatBox>
                    </UserChatDiv>
                  ) : (
                    <ChatDiv key={index}>
                      <ChatBox>
                        <ChatText>{message.message}</ChatText>
                      </ChatBox>
                    </ChatDiv>
                  )
                )}
                {loadingMessage && (
                  <ChatDiv>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <ChatBox>
                        <LoadingText />
                      </ChatBox>
                    </div>
                  </ChatDiv>
                )}
              </>
            ) : null}
          </ScrollableChat>
          <InputContainer>
            <Input
              type="text"
              placeholder={t('summarize.askQuestion')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            <img
              src={ArrowUpIcon}
              alt="send message"
              style={{ marginLeft: "auto", cursor: "pointer" }}
              onClick={handleSendMessage}
            />
          </InputContainer>
        </LeftPane>
        <RightPane>
          {file ? (
            <PdfContainer>
              <PdfViewer file={file} />
              <img
                src={CrossIcon}
                alt="close"
                style={{
                  position: "absolute",
                  zIndex: "1000",
                  right: "5px",
                  top: "5px",
                  cursor: "pointer",
                  borderRadius: "50%",
                  opacity: "0.6",
                }}
                onClick={handleRemoveFile}
              />
            </PdfContainer>
          ) : (
            <>
              <DragAndDrop
                onClick={() => document.getElementById("fileInput").click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={isDragActive ? {
                  background: '#e0e7ff',
                  border: '2px dashed #51307F',
                  color: '#51307F',
                  transition: 'background 0.2s, border 0.2s',
                } : {}}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <IconDiv>
                  <img src={UploadIcon} alt="upload" />
                </IconDiv>
                <UploadText>
                  {isDragActive ? (
                    <span style={{ color: '#51307F', fontWeight: 'bold' }}>
                      {t('summarize.releaseToUpload')}
                    </span>
                  ) : (
                    <>
                      {t('summarize.dragAndDrop')}
                      <span style={{ color: "#51307F" }}> {t('summarize.browse')}</span>
                    </>
                  )}
                </UploadText>
                <SupportText>{t('summarize.supportPdfFiles')}</SupportText>
                {error && <ErrorText>{error}</ErrorText>}
              </DragAndDrop>
              <OrDivider>{t('summarize.or')}</OrDivider>
              <DriveFilePicker onFilesSelected={handleDriveFiles} />
            </>
          )}
        </RightPane>
      </ContentDiv>

      {showRemoveFileModal && (
        <RemovePdfModal
          onCancel={handleHideRemoveFileModal}
          onRemove={() => {
            handleRemoveFile();
            handleHideRemoveFileModal();
          }}
        />
      )}
    </Container>
  );
}